import {
  SpotlightSection,
  SpotlightSection_Element,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/sections.types';
import {usePrevious} from '@aboutyou/utils';
import {useEffect, useMemo, useState} from 'react';
import {useTargetGroupIdentifier} from '../../shared-deprecated/useTargetGroupIdentifier';

/**
 * We need to ensure the data has been updated and that the request has finished fetching
 * before updating the unique data, for the spotlight slider this will ensure that we
 * update the slides only when the data is ready
 */
export const useSpotlightSliderUniqueRenderKey = (
  elements: SpotlightSection['elements'],
  isFetching?: boolean,
) => {
  const selectedTargetGroup = useTargetGroupIdentifier();
  const [uniqueKey, setUniqueKey] = useState<string>();
  const elementsSnapshot = useMemo(
    () => JSON.stringify(elements.map(getElementHeadline)),
    [elements],
  );
  const previousElementsSnapshot = usePrevious(
    elementsSnapshot,
    (current, next) => !isFetching && current !== next,
  );
  const isStreamUpdated = previousElementsSnapshot !== elementsSnapshot;

  useEffect(() => {
    // eslint-disable-next-line local-rules/set-state-in-use-effect
    setUniqueKey(String(selectedTargetGroup));
  }, []);

  useEffect(() => {
    if (
      !isFetching &&
      isStreamUpdated &&
      !uniqueKey?.includes(elementsSnapshot)
    ) {
      // need to set a unique key with the slides corresponding to the selected gender,
      // to make sure the key changes when both the selected gender and the slides are updated
      // eslint-disable-next-line local-rules/set-state-in-use-effect
      setUniqueKey(`${selectedTargetGroup}${elementsSnapshot}`);
    }
  }, [isFetching, isStreamUpdated, selectedTargetGroup, elementsSnapshot]);

  return uniqueKey;
};

const getElementHeadline = (element: SpotlightSection_Element) => {
  switch (element.type?.$case) {
    case 'campaign':
      return element.type.campaign.headline;
    case 'content':
      return element.type.content.headline;
    case 'highTierCoupon':
      return element.type.highTierCoupon.headline;
    case 'teaser':
      return element.type.teaser.headline;
    default:
      return '';
  }
};
